import { useMemo } from 'react';
import { GuidT } from '@/types/common';
import { clothingApi } from '@/api/clothingApi/clothingApi';
import { ClothingIF } from '@/api/clothingApi/types';
import { useClothingProperties } from '@/hooks/useClothingProperties';
import { useWardrobeCategories } from '@/hooks/useWardrobeCategories';
import {
    WARDROBE_CATEGORY_TABS,
    WardrobeCategoryIF,
} from '@/constants/wardrobeCategoryTabs';

export type CategoriesData = Record<string, ClothingIF[]>;

interface UseClothingByParentCategoriesIF {
    categoriesData?: CategoriesData | null;
    isLoading?: boolean;
    isError?: boolean;
}

interface ClothingWardrobeCategoryIF extends WardrobeCategoryIF {
    categoryGuid: GuidT;
}

const hasCategoryGuid = (
    category: WardrobeCategoryIF,
): category is ClothingWardrobeCategoryIF => category.categoryGuid != null;

export const useClothingByParentCategories = (
    categories?: WardrobeCategoryIF[],
): UseClothingByParentCategoriesIF => {
    const { categoriesProperties, isLoading: isPropertiesLoading } =
        useClothingProperties();
    const { wardrobeCategories } = useWardrobeCategories(categoriesProperties);

    const sourceCategories = categories ?? wardrobeCategories;

    const clothingCategories = WARDROBE_CATEGORY_TABS.filter(
        (config) => config.group === 'clothing',
    ).map((config) =>
        sourceCategories.find((category) => category.type === config.type),
    );

    const queries = clothingCategories.map((category) =>
        clothingApi.useSearchClothingQuery(
            {
                categoriesGuid:
                    category && hasCategoryGuid(category)
                        ? [category.categoryGuid]
                        : [],
            },
            {
                skip: !category || !hasCategoryGuid(category),
            },
        ),
    );

    const isLoading =
        isPropertiesLoading || queries.some((query) => query.isLoading);
    const isError = queries.some((query) => query.isError);

    const categoriesData = useMemo(() => {
        const result: CategoriesData = {};
        let hasAnyClothing = false;

        clothingCategories.forEach((category, index) => {
            if (!category || !hasCategoryGuid(category)) {
                return;
            }

            const { categoryGuid } = category;
            const categoryResponse = queries[index]?.data;
            const categoryData = categoryResponse?.data || [];
            result[categoryGuid] = categoryData;

            if (categoryData.length > 0) {
                hasAnyClothing = true;
            }
        });

        return hasAnyClothing ? result : null;
    }, [clothingCategories, queries]);

    return { categoriesData, isLoading, isError };
};
